(() => {
  if (window.AcCartActions) return;

  const cartUrl = () => window.routes?.cart_url || '/cart';
  const cartAddUrl = () => window.routes?.cart_add_url || '/cart/add.js';
  let queue = Promise.resolve();
  let revision = 0;
  let pending = 0;
  let welcomeController;
  const failures = new Map();
  const notify = () => document.dispatchEvent(new CustomEvent('ac:cart-write-state', { detail: { pending, revision } }));
  const enqueue = (key, operation, { optional = false } = {}) => {
    pending += 1;
    revision += 1;
    notify();
    const result = queue.then(operation).then((payload) => {
      failures.delete(key);
      return payload;
    }, (error) => {
      if (optional || error.cartRestored) failures.delete(key);
      else failures.set(key, error);
      throw error;
    }).finally(() => { pending -= 1; revision += 1; notify(); });
    queue = result.catch(() => {});
    return key === 'add' ? result.catch((error) => {
      // A rejected add can still add the available quantity on Shopify.
      document.dispatchEvent(new CustomEvent('ac:cart-updated', { detail: { refreshed: false } }));
      throw error;
    }) : result;
  };
  const settled = async () => {
    let snapshot;
    do { snapshot = queue; await snapshot; } while (snapshot !== queue);
  };
  const checkoutReady = async () => {
    cancelWelcomeDiscount();
    await settled();
    const failedAdd = failures.get('add');
    if (failedAdd && await sync()) {
      // Confirm server state without replaying an add that may already have succeeded.
      if (failures.get('add') === failedAdd) failures.delete('add');
    }
    if (failures.size) throw failures.values().next().value;
  };
  const cartError = (payload, fallback) => {
    const message = [payload?.description, payload?.message].find((value) => typeof value === 'string' && value);
    const error = new Error(message || fallback);
    error.errors = payload?.errors || (typeof payload?.description === 'object' ? payload.description : undefined);
    return error;
  };
  const post = async (route, data, { signal, bundled = true } = {}) => {
    const response = await fetch(route, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify(bundled ? { ...data, sections: ['ac-cart-drawer-state'], sections_url: cartUrl() } : data),
      signal,
    });
    const payload = await response.json().catch(() => { throw new Error('Unable to update your bag. Please try again.'); });
    if (!response.ok) throw cartError(payload, 'Unable to update your bag.');
    return payload;
  };
  const change = (key, quantity, failureKey = key) => enqueue(`line:${failureKey}`, () => post(window.routes?.cart_change_url || '/cart/change.js', { id: key, quantity }));
  const addItems = (items) => enqueue('add', () => post(cartAddUrl(), { items }));
  const update = (data) => enqueue('discount' in data ? 'discount' : 'note', () => post(window.routes?.cart_update_url || '/cart/update.js', data));
  // Optional notes never hold the cart write queue. Checkout posts the current field natively.
  const saveNote = (note, signal) => post((window.Shopify?.routes?.root || '/') + 'cart/update.js', { note }, { signal, bundled: false });

  const readCart = async (signal) => {
    const response = await fetch(cartUrl() + '.js', { signal, cache: 'no-store', headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error(document.querySelector('ac-cart-overlay')?.dataset.errorLabel);
    return response.json();
  };
  const cancelWelcomeDiscount = () => welcomeController?.abort();
  const applyWelcomeDiscount = (code) => {
    cancelWelcomeDiscount();
    const controller = new AbortController();
    welcomeController = controller;
    // Optional promotion work must release the shared queue, including on checkout.
    const timer = setTimeout(() => controller.abort(), 3000);
    return enqueue('welcome-discount', async () => {
      if (controller.signal.aborted) throw controller.signal.reason;
      const cart = await readCart(controller.signal);
      if (!cart.item_count) return cart;
      if (!Array.isArray(cart.discount_codes)) throw new Error('Discount data unavailable');
      const codes = cart.discount_codes.map(entry => entry.code);
      if (codes.some(existing => existing.toLowerCase() === code.toLowerCase())) return cart;
      return post(window.routes?.cart_update_url || '/cart/update.js', { discount: [...codes, code].join(',') }, { signal: controller.signal, bundled: false });
    }, { optional: true }).finally(() => {
      clearTimeout(timer);
      if (welcomeController === controller) welcomeController = null;
    });
  };
  const identity = (item, variant = item.variant_id) => JSON.stringify([
    Number(variant), Object.entries(item.properties || {}).sort(([a], [b]) => a.localeCompare(b)),
    item.selling_plan_allocation?.selling_plan?.id || null,
  ]);
  // Keep an uncertain replacement until it is reconciled. Retrying must never replay an add.
  const replacements = new Map();
  const replace = (original, variant) => enqueue('replace:' + original.key, async () => {
    const fail = () => new Error(document.querySelector('ac-cart-overlay')?.dataset.errorLabel);
    const find = (cart, signature) => {
      const matches = cart.items.filter(item => identity(item) === signature);
      if (matches.length > 1) throw fail();
      return matches[0];
    };
    const recover = async (transaction) => {
      const cart = await readCart();
      const source = find(cart, transaction.source);
      const target = find(cart, transaction.target);
      if (!source && target?.quantity === transaction.before + transaction.quantity) return cart;
      if (!source || source.quantity !== transaction.quantity) throw fail();
      const added = (target?.quantity || 0) - transaction.before;
      if (added < 0 || added > transaction.quantity) throw fail();
      if (added) await post(window.routes?.cart_change_url || '/cart/change.js', { id: target.key, quantity: transaction.before }, { bundled: false });
      return null;
    };
    const previous = replacements.get(original.key);
    if (previous) {
      const completed = await recover(previous);
      replacements.delete(original.key);
      if (completed) return completed;
    }
    const cart = await readCart();
    const source = find(cart, identity(original));
    if (!source || source.quantity !== original.quantity) throw fail();
    if (Number(variant) === source.variant_id) return cart;
    const targetSignature = identity(source, variant);
    const transaction = { source: identity(source), target: targetSignature,
      before: find(cart, targetSignature)?.quantity || 0, quantity: source.quantity };
    replacements.set(original.key, transaction);
    try {
      const item = { id: Number(variant), quantity: source.quantity, properties: source.properties || {} };
      const plan = source.selling_plan_allocation?.selling_plan?.id;
      if (plan) item.selling_plan = plan;
      // Native add validates stock, including when the target already exists in the bag.
      await post(cartAddUrl(), { items: [item] }, { bundled: false });
      const confirmed = await readCart();
      const current = find(confirmed, transaction.source);
      const target = find(confirmed, transaction.target);
      if (!current || current.quantity !== source.quantity || target?.quantity !== transaction.before + source.quantity) throw fail();
      const result = await post(window.routes?.cart_change_url || '/cart/change.js', { id: current.key, quantity: 0 });
      replacements.delete(original.key);
      return result;
    } catch (error) {
      try {
        const completed = await recover(transaction);
        replacements.delete(original.key);
        if (completed) return completed;
        error.cartRestored = true;
      } catch { /* Preserve the transaction: a retry must reconcile before attempting any add. */ }
      document.dispatchEvent(new CustomEvent('ac:cart-updated', { detail: { refreshed: false } }));
      throw error;
    }
  });

  const announce = (message) => {
    if (!message) return;
    document.querySelector('ac-overlay-system')?.announce?.(message);
  };

  const add = async (form) => {
    if (!(form instanceof HTMLFormElement)) throw new TypeError('A product form is required.');

    const body = new FormData(form);
    const submission = {};
    const dispatch = (type, detail = {}) => form.dispatchEvent(new CustomEvent(`ac:product-form-${type}`, {
      detail: { submission, ...detail },
    }));
    dispatch('submit');
    return enqueue('add', async () => {
      const response = await fetch(cartAddUrl(), {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body,
      });
      const payload = await response.json().catch(() => { throw new Error('Unable to add this item. Please try again.'); });
      if (!response.ok) throw cartError(payload, 'Unable to add this item.');
      return payload;
    }).then((item) => {
      dispatch('success', { item });
      return item;
    }, (error) => {
      dispatch('error', { error });
      throw error;
    });
  };

  // Default callers receive cart data; immediate-open callers opt into a single-use receipt.
  const sync = async (item, { forOpen = false } = {}) => {
    const overlay = document.querySelector('ac-cart-overlay');
    const cart = await overlay?.refresh?.();
    document.dispatchEvent(
      new CustomEvent('ac:cart-updated', {
        detail: { item, cart: cart || null, refreshed: Boolean(cart) },
      })
    );
    return forOpen ? overlay?.createOpenReceipt?.(cart) || null : cart || null;
  };

  const open = (receipt = null) => {
    const trigger = document.querySelector('[data-ac-ref-open="cart"]');
    const overlay = document.querySelector('ac-cart-overlay');
    if (trigger && receipt && overlay?.openWithReceipt) overlay.openWithReceipt(receipt, trigger);
    else if (trigger) trigger.click();
    else window.location.assign(cartUrl());
  };

  const addAndOpen = async (form, successMessage) => {
    const item = await add(form);
    const receipt = await sync(item, { forOpen: true });
    announce(successMessage);
    open(receipt);
    return item;
  };

  window.AcCartActions = Object.freeze({ add, addItems, sync, open, announce, addAndOpen, change, update, saveNote, replace, applyWelcomeDiscount, cancelWelcomeDiscount, settled, checkoutReady,
    get revision() { return revision; }, get pending() { return pending; } });
})();
